class StickyBuyButton extends HTMLElement {
  constructor() {
    super();

    this.activeClass = "wt-product__sticky-buy--show";
  }

  connectedCallback() {
    this.initialize();
  }

  initialize() {
    const addToCartModule = document.querySelector(".wt-product__add-to-cart");
    const btn = this.querySelector("button");
    const mode = this.dataset.mode || "scroll";
    const realBtn = addToCartModule
      ? addToCartModule.querySelector(".js-add-to-cart")
      : null;

    // Mirror mode: clone label/state/disabled from real ATC and forward clicks.
    // Falls back to scroll mode if the real ATC isn't on the page.
    const useMirror = mode === "mirror" && !!realBtn;

    if (useMirror) {
      this.setupMirror(btn, realBtn);
    } else {
      const addToCart = this.dataset.addToCart === "";
      btn.addEventListener("click", (e) => {
        if (!addToCart) {
          e.preventDefault();
        }
        if (addToCartModule) {
          addToCartModule.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      });
    }

    const forObserver = document.querySelectorAll(
      ".wt-product__add-to-cart, .wt-footer, .wt-product__name",
    );

    let intersected = [];

    // Publish current sticky-buy height to :root as --sticky-buy-offset so any
    // floating overlays (Attentive button, Klaviyo, etc.) can lift above it via
    // bottom: calc(var(--sticky-buy-offset, 0px) + Npx).
    const updateOffset = () => {
      const isShown = this.classList.contains(this.activeClass);
      // Only meaningful below the desktop breakpoint (sticky bar is hidden ≥1200px via CSS).
      const onMobile = window.matchMedia("(max-width: 1199px)").matches;
      const px = isShown && onMobile ? `${this.offsetHeight}px` : "0px";
      document.documentElement.style.setProperty("--sticky-buy-offset", px);
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(({ isIntersecting, target }) => {
        isIntersecting
          ? intersected.push(target)
          : (intersected = intersected.filter((item) => item !== target));
      });

      if (intersected.length) {
        this.classList.remove(this.activeClass);
      } else {
        this.classList.add(this.activeClass);
      }
      updateOffset();
    });

    forObserver.forEach((item) => {
      observer.observe(item);
    });

    window.addEventListener("resize", updateOffset);
    updateOffset();
  }

  // Mirror the real Add to Cart button: copy its visible label/price/suffix,
  // disabled state and primary/secondary class, then forward clicks. This
  // means the sticky bar always reflects variant availability, sold-out
  // copy, dynamic accessory totals, and AJAX-add loading state without
  // duplicating any of the real ATC logic.
  setupMirror(stickyBtn, realBtn) {
    const stickyLabel = stickyBtn.querySelector(".js-sticky-buy-label");

    // The real ATC's visible content lives in .js-atc-button-main-content
    // (label-base + price + optional suffix). Cloning that subtree keeps
    // structure & styling intact for the sticky bar.
    const sourceContent = () =>
      realBtn.querySelector(".js-atc-button-main-content");

    const sync = () => {
      const src = sourceContent();
      if (src && stickyLabel) {
        stickyLabel.innerHTML = src.innerHTML;
      } else if (stickyLabel) {
        // Fallback to the real button's text content if structure changed.
        stickyLabel.textContent = realBtn.textContent.trim();
      }

      // Mirror disabled state.
      const isDisabled =
        realBtn.disabled ||
        realBtn.getAttribute("aria-disabled") === "true" ||
        realBtn.classList.contains("is-disabled");
      stickyBtn.disabled = isDisabled;
      stickyBtn.classList.toggle("is-disabled", isDisabled);
      if (isDisabled) {
        stickyBtn.setAttribute("aria-disabled", "true");
      } else {
        stickyBtn.removeAttribute("aria-disabled");
      }

      // Mirror primary/secondary variant class so the sticky button picks up
      // the merchant's chosen ATC styling. Keep our base width/full-width
      // classes intact.
      stickyBtn.classList.toggle(
        "wt-button--primary",
        realBtn.classList.contains("wt-button--primary"),
      );
      stickyBtn.classList.toggle(
        "wt-button--secondary",
        realBtn.classList.contains("wt-button--secondary") ||
          !realBtn.classList.contains("wt-button--primary"),
      );

      // Mirror loading state (button-loading-indicator toggles a class on
      // the real button while AJAX add is in flight).
      stickyBtn.classList.toggle(
        "loading",
        realBtn.classList.contains("loading"),
      );
    };

    // Forward clicks to the real ATC. .click() preserves type=submit / form
    // semantics on the real button so cart add behaves exactly as if the
    // user tapped the visible Add to Cart button.
    stickyBtn.addEventListener("click", (e) => {
      e.preventDefault();
      if (stickyBtn.disabled) return;
      realBtn.click();
    });

    // React to label/state/class changes on the real ATC.
    const mo = new MutationObserver(sync);
    mo.observe(realBtn, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["class", "disabled", "aria-disabled", "data-loading"],
    });

    // Belt-and-suspenders: re-sync on cart updates and on a custom
    // accessory-staging event so totals reflect bundled accessories
    // immediately. The real ATC label is updated by variant.js and accessory
    // staging code via the same DOM, so the MutationObserver above handles
    // most cases on its own.
    if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
      try {
        subscribe(PUB_SUB_EVENTS.cartUpdate, sync);
      } catch (_) {}
    }
    document.addEventListener("pdp:accessory-staging-changed", sync);
    document.addEventListener("variant:change", sync);

    // Initial sync.
    sync();
  }
}

customElements.define("sticky-buy-button", StickyBuyButton);

if (!customElements.get("gallery-fashion")) {
  customElements.define(
    "gallery-fashion",
    class GalleryFashion extends HTMLElement {
      constructor() {
        super();
        this.section = this.closest("section");
        this.logoBanner = this;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.removeEventsWhenDesignMode();
      }

      addEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.addEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.addEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      removeEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.removeEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.removeEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      reinitAfterDelay() {
        setTimeout(() => this.reinit(), 0);
      }

      isFirstSection() {
        const sectionWrapper = document.querySelector("#root");
        const firstSection = sectionWrapper.querySelector("section");
        this.section = this.closest("section");
        const currentSection = this.section;

        return firstSection === currentSection;
      }

      handleResize() {
        this.setTopMargin();
        this.positioningProductInfo();
      }

      observeHeader() {
        const header = document.querySelector(".wt-header");
        const activeTransparentClass = "wt-header--fashion-transparent";

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                header.classList.remove(activeTransparentClass);
              } else {
                header.classList.add(activeTransparentClass);
              }
            });
          },
          { root: null, threshold: 0.05 },
        );

        observer.observe(this.querySelector(".wt-product__gallery"));
      }

      calculateOffset() {
        const header = document.querySelector("header");
        const headerHeight = header.offsetHeight;
        const offset =
          this.isTransparentHeaderEnabled() && this.isFirstSection()
            ? headerHeight
            : 0;

        return offset;
      }

      setTopMargin() {
        const offset = this.calculateOffset();
        if (offset) {
          this.section.style.marginTop = `-${offset}px`;
        } else {
          this.section.style.marginTop = 0;
        }
      }

      isTransparentHeaderEnabled() {
        const header = document.querySelector(".wt-header");
        return (
          header.dataset.transparent &&
          header.classList.contains("wt-header--v3")
        );
      }

      renderProgressBar() {
        const thumbsGallery = this.querySelector("[data-thumbs]");
        const progressBarElement = document.createElement("div");
        progressBarElement.classList.add("gallery-fashion__progress-bar");
        const progressBarIndicatorElement = document.createElement("div");
        progressBarIndicatorElement.classList.add(
          "gallery-fashion__progress-bar-indicator",
        );
        progressBarElement.appendChild(progressBarIndicatorElement);
        thumbsGallery.appendChild(progressBarElement);

        function updateProgressBar() {
          const images = this.querySelector("gallery-section");
          const progressBar = progressBarIndicatorElement;

          const scrolled = window.scrollY;
          const maxHeight = images.clientHeight - window.innerHeight;
          const scrollPercentage = (scrolled / maxHeight) * 100;

          const progressBarHeight = progressBarElement.clientHeight;
          const progressHeight = progressBar.clientHeight;
          const maxProgressTop = progressBarHeight - progressHeight;
          const progressTop = Math.min(
            (scrollPercentage / 100) * maxProgressTop,
            maxProgressTop,
          );

          progressBar.style.top = `${progressTop}px`;

          if (progressTop === maxProgressTop) {
            thumbsGallery.classList.add("finished");
          } else {
            thumbsGallery.classList.remove("finished");
          }
        }

        window.addEventListener("scroll", updateProgressBar.bind(this));

        function handleThumbsClick(event) {
          if (event.target.closest(".thumbs-list-item")) {
            const thumbnails = Array.from(
              event.currentTarget.querySelectorAll(".wt-product__img"),
            );
            const targetImage = event.target
              .closest(".thumbs-list-item")
              .querySelector(".wt-product__img");
            const index = thumbnails.indexOf(targetImage);
            const fullImage = this.querySelectorAll(
              ".wt-masonry__wrapper .wt-product__img, .wt-masonry__wrapper .wt-product__thumbnail-video",
            )[index];

            fullImage.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }

        thumbsGallery.addEventListener("click", handleThumbsClick.bind(this));
      }

      positioningProductInfo() {
        const productInfo = this.querySelector(".wt-product__main");
        const mediaDesktop = window.matchMedia("(min-width: 1200px)");

        if (mediaDesktop.matches) {
          productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
        }

        const resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            if (mediaDesktop.matches) {
              productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
            }
          }
        });

        const observedElement = productInfo;
        resizeObserver.observe(observedElement);
      }

      attachEvents() {
        window.addEventListener("resize", this.handleResize.bind(this));
      }

      /**
       * Wait until all images inside masonry wrapper are loaded
       * then run callback
       */
      waitForMasonryImages(callback) {
        const masonry = this.querySelector(".swiper-wrapper--masonry");
        if (!masonry) return;

        const images = masonry.querySelectorAll("img");
        if (images.length === 0) {
          callback();
          return;
        }

        let loaded = 0;
        const checkDone = () => {
          loaded++;
          if (loaded === images.length) {
            callback();
          }
        };

        images.forEach((img) => {
          if (img.complete) {
            checkDone();
          } else {
            img.addEventListener("load", checkDone, { once: true });
            img.addEventListener("error", checkDone, { once: true });
          }
        });
      }

      init() {
        this.reinitAfterDelay = this.reinitAfterDelay.bind(this);
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            this.waitForMasonryImages(() => {
              const masonry = this.querySelector(".swiper-wrapper--masonry");
              if (masonry) {
                stickyHeaderThreshold.style.height = `${masonry.offsetHeight}px`;
              }
            });
          }
          this.setTopMargin();
          this.observeHeader();
          this.attachEvents();
        }

        this.renderProgressBar();
        this.positioningProductInfo();
        this.addEventsWhenDesignMode();
      }

      reinit() {
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = `${this.querySelector(".swiper-wrapper--masonry").offsetHeight}px`;
          }
          this.setTopMargin();
          this.observeHeader();
        }
        this.positioningProductInfo();
      }
    },
  );
}
